import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef} from 'react';

export interface MarcadorInfo { caminho: string; rotulo: string; exemplo: string | null; }

export interface ContratoModeloEditorHandle {
    inserirMarcador: (caminho: string) => void;
    comando: (comando: string, valor?: string) => void;
    quebrarPagina: () => void;
}

interface Props {
    valor: string;
    onChange: (html: string) => void;
    marcadores: MarcadorInfo[];
    desabilitado: boolean;
    base: string;
    escala: number;
    /** Cabeçalho com `__PAGINA__` e `__TOTAL__` no lugar dos números. */
    cabecalho: string;
    rodape: string;
    estilos: string[];
    quebra: string;
    /** O bloco de assinaturas renderizado, como sai no documento. */
    assinaturas: string;
}

/** Largura da página A4 em px (210mm a 96dpi). */
export const LARGURA_A4 = 794;

/** Vai depois de cada etiqueta inserida, para o cursor ter onde parar; sai ao salvar. */
const ESPACO_ZERO = String.fromCharCode(0x200b);

const MARCADOR = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

/** O mesmo critério do `GeradorDeContrato::paginas()`. */
const QUEBRA = /<div[^>]*class="[^"]*\bquebra-pagina\b[^"]*"[^>]*>\s*<\/div>/i;

const escapar = (texto: string) => texto
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * O corpo do modelo editado direto nas folhas, com o CSS do contrato.
 *
 * Cada trecho entre quebras de página é uma folha A4 com cabeçalho e rodapé reais,
 * e cada folha tem a sua área editável. A paginação é manual: "Quebra de página"
 * divide a folha no cursor, e o botão entre as folhas (ou Backspace no começo de
 * uma) junta de volta. Folha cujo texto passa do rodapé fica marcada em vermelho,
 * porque no documento gerado esse texto sairia por cima do rodapé.
 *
 * Os marcadores viram etiquetas não editáveis com o valor de exemplo; ao salvar,
 * cada etiqueta volta a ser `{{caminho}}`.
 *
 * O iframe só é remontado quando o valor muda por fora (carregar o modelo, editar
 * na aba HTML). O que o próprio editor emitiu não remonta, senão o cursor pularia
 * para o começo a cada tecla.
 */
const ContratoModeloEditor = forwardRef<ContratoModeloEditorHandle, Props>((props, ref) => {
    const {valor, marcadores, desabilitado, base, escala, cabecalho, rodape, estilos, quebra, assinaturas} = props;
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const ultimoEmitido = useRef<string | null>(null);
    const faixa = useRef<Range | null>(null);
    const onChangeRef = useRef(props.onChange);
    onChangeRef.current = props.onChange;
    const porCaminho = useRef<Record<string, MarcadorInfo>>({});
    porCaminho.current = Object.fromEntries(marcadores.map((m) => [m.caminho, m]));
    const moldura = useRef({ cabecalho, rodape, quebra, desabilitado, assinaturas });
    moldura.current = { cabecalho, rodape, quebra, desabilitado, assinaturas };

    const etiqueta = useCallback((caminho: string) => {
        const info = porCaminho.current[caminho];
        if (caminho === 'assinaturas') {
            // Nasce vazio e é preenchido por `preencherBlocos()`: o bloco tem <div>, e se
            // passasse pelo parser dentro de um <p> o parser fecharia o parágrafo sozinho.
            return `<span class="mk-bloco" contenteditable="false" draggable="true" data-mk="assinaturas" title="{{assinaturas}} · vem dos espaços de assinatura do modelo"></span>`;
        }
        if (!info) {
            return `<span class="mk mk-erro" contenteditable="false" draggable="true" data-mk="${escapar(caminho)}" title="Marcador desconhecido">[[${escapar(caminho)}]]</span>`;
        }
        return `<span class="mk" contenteditable="false" draggable="true" data-mk="${escapar(caminho)}" title="{{${escapar(caminho)}}} · ${escapar(info.rotulo)}">${escapar(info.exemplo || info.rotulo)}</span>`;
    }, []);

    const doc = (): Document | null => iframeRef.current?.contentDocument || null;
    const areas = (): HTMLElement[] => Array.from(doc()?.querySelectorAll<HTMLElement>('.folha-area') || []);
    // Sem `instanceof HTMLElement`: o nó é do iframe, que tem o próprio HTMLElement,
    // e o teste contra o da janela de fora dá sempre falso.
    const areaDe = (no: Node | null): HTMLElement | null => {
        let atual: Node | null = no;
        while (atual && !(atual.nodeType === 1 && (atual as Element).classList.contains('folha-area'))) atual = atual.parentNode;
        return atual as HTMLElement | null;
    };

    const serializarArea = (area: HTMLElement): string => {
        const copia = area.cloneNode(true) as HTMLElement;
        copia.querySelectorAll('p[data-auto]').forEach((p) => {
            if (!(p.textContent || '').split(ESPACO_ZERO).join('').trim() && !p.querySelector('span.mk, span.mk-bloco')) p.remove();
            else p.removeAttribute('data-auto');
        });
        copia.querySelectorAll('span.mk, span.mk-bloco').forEach((span) => {
            span.replaceWith(copia.ownerDocument.createTextNode(`{{${span.getAttribute('data-mk')}}}`));
        });
        return copia.innerHTML.split(ESPACO_ZERO).join('');
    };

    const emitir = () => {
        const html = areas().map(serializarArea).join(moldura.current.quebra);
        ultimoEmitido.current = html;
        onChangeRef.current(html);
    };

    /**
     * Etiqueta não editável como último nó da folha (ou colada em outra, no nível
     * da folha) não deixa lugar para o cursor. Um parágrafo vazio automático
     * resolve; se continuar vazio, a serialização descarta.
     */
    const garantirParagrafos = () => {
        const d = doc();
        if (!d) return;
        areas().forEach((area) => {
            const ultimo = area.lastChild;
            const ultimoEhEtiqueta = ultimo && ultimo.nodeType === 1 && (ultimo as Element).matches('span.mk, span.mk-bloco');
            const ultimoEhTextoVazio = ultimo && ultimo.nodeType === 3 && !(ultimo.textContent || '').split(ESPACO_ZERO).join('').trim()
                && ultimo.previousSibling && ultimo.previousSibling.nodeType === 1 && (ultimo.previousSibling as Element).matches('span.mk-bloco');
            if (ultimoEhEtiqueta || ultimoEhTextoVazio) {
                const p = d.createElement('p');
                p.setAttribute('data-auto', '1');
                p.innerHTML = '<br>';
                area.appendChild(p);
            }
            const primeiro = area.firstChild;
            if (primeiro && primeiro.nodeType === 1 && (primeiro as Element).matches('span.mk-bloco')) {
                const p = d.createElement('p');
                p.setAttribute('data-auto', '1');
                p.innerHTML = '<br>';
                area.insertBefore(p, primeiro);
            }
        });
    };

    /** O bloco de assinaturas de verdade dentro de cada etiqueta, via DOM. */
    const preencherBlocos = () => {
        doc()?.querySelectorAll<HTMLElement>('span.mk-bloco').forEach((span) => {
            span.innerHTML = moldura.current.assinaturas || 'Blocos de assinatura';
        });
    };

    const verificarEstouro = () => {
        areas().forEach((area) => {
            area.closest('.folha')?.classList.toggle('estourou', area.scrollHeight > area.clientHeight + 2);
        });
    };

    const cabecalhoDa = (pagina: number, total: number) => moldura.current.cabecalho
        .split('__PAGINA__').join(String(pagina))
        .split('__TOTAL__').join(String(total));

    /** Refaz os números do cabeçalho e os botões entre as folhas. */
    const renumerar = () => {
        const d = doc();
        if (!d) return;
        const folhas = Array.from(d.querySelectorAll<HTMLElement>('.folha'));
        d.querySelectorAll('.entre-folhas').forEach((el) => el.remove());
        folhas.forEach((folha, indice) => {
            const antigo = folha.querySelector('.page-header');
            const molde = d.createElement('template');
            molde.innerHTML = cabecalhoDa(indice + 1, folhas.length);
            const novo = molde.content.firstElementChild;
            if (antigo && novo) antigo.replaceWith(novo);
            if (indice > 0 && !moldura.current.desabilitado) {
                const entre = d.createElement('div');
                entre.className = 'entre-folhas';
                entre.innerHTML = `<button type="button" data-juntar="${indice}" title="Junta esta folha com a anterior">Remover quebra de página</button>`;
                folha.before(entre);
            }
        });
        verificarEstouro();
    };

    const folhaHtml = (conteudo: string, pagina: number, total: number) => `<div class="page page-contrato folha">
${cabecalhoDa(pagina, total)}
<div class="folha-area" contenteditable="${moldura.current.desabilitado ? 'false' : 'true'}" spellcheck="true">${conteudo || '<p><br></p>'}</div>
${moldura.current.rodape}
<div class="aviso-estouro">O texto desta página passa do rodapé. Quebre a página antes.</div>
</div>`;

    const cursorEm = (no: Node, posicao: number) => {
        const d = doc();
        const selecao = d?.getSelection();
        if (!d || !selecao) return;
        const cursor = d.createRange();
        cursor.setStart(no, posicao);
        cursor.collapse(true);
        selecao.removeAllRanges();
        selecao.addRange(cursor);
        faixa.current = cursor.cloneRange();
    };

    const juntar = (indice: number) => {
        const lista = areas();
        const atual = lista[indice];
        const anterior = lista[indice - 1];
        if (!atual || !anterior) return;
        const pontoDeJuncao = anterior.childNodes.length;
        while (atual.firstChild) anterior.appendChild(atual.firstChild);
        atual.closest('.folha')?.remove();
        renumerar();
        anterior.focus();
        cursorEm(anterior, Math.min(pontoDeJuncao, anterior.childNodes.length));
        emitir();
    };

    const quebrar = () => {
        const d = doc();
        const selecao = restaurarSelecao();
        if (!d || !selecao || selecao.rangeCount === 0) return;
        const faixaAtual = selecao.getRangeAt(0);
        const area = areaDe(faixaAtual.startContainer);
        if (!area) return;

        // Do cursor até o fim da folha vai para a folha nova.
        const resto = d.createRange();
        resto.setStart(faixaAtual.startContainer, faixaAtual.startOffset);
        resto.setEnd(area, area.childNodes.length);
        const conteudo = resto.extractContents();

        const molde = d.createElement('template');
        molde.innerHTML = folhaHtml('', 0, 0);
        const folhaNova = molde.content.firstElementChild as HTMLElement;
        const areaNova = folhaNova.querySelector('.folha-area') as HTMLElement;
        areaNova.innerHTML = '';
        areaNova.appendChild(conteudo);
        // Elementos que a divisão deixou vazios (o fim do parágrafo cortado) saem.
        [area, areaNova].forEach((alvo) => {
            alvo.querySelectorAll('p, h1, h2, li').forEach((el) => {
                if (!el.textContent?.split(ESPACO_ZERO).join('').trim() && !el.querySelector('span.mk, br, img')) el.remove();
            });
            if (!alvo.innerHTML.trim()) alvo.innerHTML = '<p><br></p>';
        });
        area.closest('.folha')?.after(folhaNova);
        renumerar();
        areaNova.focus();
        cursorEm(areaNova, 0);
        emitir();
    };

    const montar = useCallback(() => {
        const d = doc();
        if (!d) return;
        const paginas = (valor || '').split(QUEBRA);
        const folhas = paginas
            .map((trecho, indice) => folhaHtml(trecho.replace(MARCADOR, (_: string, caminho: string) => etiqueta(caminho)), indice + 1, paginas.length))
            .join('\n');

        d.open();
        d.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><base href="${base}/"/>
${estilos.map((href) => `<link rel="stylesheet" href="${href}"/>`).join('\n')}
<style>
html{zoom:${escala.toFixed(3)}}
body{margin:0;padding:24px 0;background:#e9ecef}
.page{margin:0 auto !important;background:#fff;box-shadow:0 2px 12px rgba(0,0,0,.15)}
.folha-area{height:100%;outline:none}
.folha.estourou{box-shadow:0 0 0 2px #b42318,0 2px 12px rgba(0,0,0,.15)}
.aviso-estouro{display:none;position:absolute;left:20mm;right:20mm;bottom:22mm;padding:4px 8px;border-radius:4px;background:#b42318;color:#fff;font-size:11px;text-align:center}
.folha.estourou .aviso-estouro{display:block}
.entre-folhas{display:flex;justify-content:center;margin:10px 0}
.entre-folhas button{font:12px Inter,sans-serif;color:#6c757d;background:#fff;border:1px dashed #adb5bd;border-radius:14px;padding:3px 12px;cursor:pointer}
.entre-folhas button:hover{color:#b42318;border-color:#b42318}
.mk{background:#fff3cd;border-bottom:1.5px solid #e6ab32;border-radius:3px;padding:0 2px;cursor:grab;-webkit-box-decoration-break:clone;box-decoration-break:clone}
.mk-erro{background:#fde8e7;border-color:#b42318;color:#b42318}
.mk-bloco{display:block;position:relative;cursor:grab;border-radius:4px;outline:1.5px dashed transparent;outline-offset:6px}
.mk-bloco:hover{outline-color:#e6ab32}
.mk-bloco:hover::before{content:'Bloco de assinaturas · arraste para mover, clique e Delete para remover';position:absolute;top:-26px;left:0;font:11px Inter,sans-serif;color:#8a6d1d;background:#fff3cd;padding:1px 6px;border-radius:3px}
.mk-bloco .assinaturas{margin-top:0}
</style></head><body>${folhas}</body></html>`);
        d.close();

        d.addEventListener('input', () => { garantirParagrafos(); emitir(); verificarEstouro(); });

        // Clicar numa etiqueta seleciona ela inteira: Delete apaga, digitar substitui.
        d.addEventListener('click', (evento) => {
            const etiquetaClicada = (evento.target as Element).closest?.('span.mk, span.mk-bloco');
            if (!etiquetaClicada || !areaDe(etiquetaClicada)) return;
            const selecao = d.getSelection();
            const faixaEtiqueta = d.createRange();
            faixaEtiqueta.selectNode(etiquetaClicada);
            selecao?.removeAllRanges();
            selecao?.addRange(faixaEtiqueta);
            faixa.current = faixaEtiqueta.cloneRange();
        });

        // Arrastar uma etiqueta move ela para onde for solta, inclusive em outra folha.
        let arrastando: Element | null = null;
        d.addEventListener('dragstart', (evento: DragEvent) => {
            const etiquetaArrastada = (evento.target as Element).closest?.('span.mk, span.mk-bloco');
            if (!etiquetaArrastada || moldura.current.desabilitado) return;
            arrastando = etiquetaArrastada;
            evento.dataTransfer?.setData('text/plain', '');
            if (evento.dataTransfer) evento.dataTransfer.effectAllowed = 'move';
        });
        d.addEventListener('dragover', (evento: DragEvent) => {
            if (arrastando && areaDe(evento.target as Node)) evento.preventDefault();
        });
        d.addEventListener('drop', (evento: DragEvent) => {
            if (!arrastando) return;
            evento.preventDefault();
            const destino = (d as any).caretRangeFromPoint?.(evento.clientX, evento.clientY) as Range | null;
            if (!destino || !areaDe(destino.startContainer) || arrastando.contains(destino.startContainer)) {
                arrastando = null;
                return;
            }
            // O espaço invisível que acompanhava a etiqueta vai junto.
            const seguinte = arrastando.nextSibling;
            if (seguinte && seguinte.nodeType === 3 && seguinte.textContent === ESPACO_ZERO) seguinte.remove();
            const acompanhante = d.createTextNode(ESPACO_ZERO);
            const areaDestino = areaDe(destino.startContainer) as HTMLElement;
            if (arrastando.matches('span.mk-bloco')) {
                // Bloco não entra no meio de uma linha: vai antes ou depois do parágrafo
                // mais próximo, conforme a metade em que foi solto.
                let bloco: Node = destino.startContainer;
                while (bloco.parentNode && bloco.parentNode !== areaDestino) bloco = bloco.parentNode;
                if (bloco === areaDestino) {
                    areaDestino.appendChild(arrastando);
                } else {
                    const caixa = (bloco as Element).getBoundingClientRect?.();
                    const depois = caixa ? evento.clientY > caixa.top + caixa.height / 2 : true;
                    areaDestino.insertBefore(arrastando, depois ? bloco.nextSibling : bloco);
                }
                arrastando.after(acompanhante);
            } else {
                destino.insertNode(acompanhante);
                destino.insertNode(arrastando);
            }
            arrastando = null;
            garantirParagrafos();
            cursorEm(acompanhante, 1);
            emitir();
            verificarEstouro();
        });
        d.addEventListener('dragend', () => { arrastando = null; });
        d.addEventListener('click', (evento) => {
            const alvo = (evento.target as HTMLElement).closest('[data-juntar]');
            if (alvo) juntar(Number(alvo.getAttribute('data-juntar')));
        });
        // Colar só texto: o que vem do Word traz estilo que o contrato não quer.
        d.addEventListener('paste', (evento: ClipboardEvent) => {
            if (!areaDe(evento.target as Node)) return;
            evento.preventDefault();
            d.execCommand('insertText', false, evento.clipboardData?.getData('text/plain') || '');
        });
        d.addEventListener('keydown', (evento: KeyboardEvent) => {
            if (evento.key !== 'Backspace') return;
            const selecao = d.getSelection();
            if (!selecao || !selecao.isCollapsed || selecao.rangeCount === 0) return;
            const faixaAtual = selecao.getRangeAt(0);
            const area = areaDe(faixaAtual.startContainer);
            if (!area) return;

            // Backspace no começo de uma folha junta com a anterior.
            const antes = d.createRange();
            antes.setStart(area, 0);
            antes.setEnd(faixaAtual.startContainer, faixaAtual.startOffset);
            const indice = areas().indexOf(area);
            if (indice > 0 && !antes.toString().split(ESPACO_ZERO).join('') && !antes.cloneContents().querySelector('span.mk')) {
                evento.preventDefault();
                juntar(indice);
                return;
            }

            // O espaço invisível depois da etiqueta não pode custar um Backspace a mais.
            const no = selecao.anchorNode;
            const posicao = selecao.anchorOffset;
            if (no && no.nodeType === 3 && posicao > 0 && (no.textContent || '')[posicao - 1] === ESPACO_ZERO) {
                (no as Text).deleteData(posicao - 1, 1);
                cursorEm(no, posicao - 1);
            }
        });
        // A posição do cursor é guardada nos eventos que de fato chegam: o
        // `selectionchange` não dispara num documento escrito com `document.open()`.
        const guardarCursor = () => {
            const selecao = d.getSelection();
            if (selecao && selecao.rangeCount > 0 && areaDe(selecao.anchorNode)) {
                faixa.current = selecao.getRangeAt(0).cloneRange();
            }
        };
        ['mouseup', 'keyup', 'input', 'focusout'].forEach((tipo) => d.addEventListener(tipo, guardarCursor, true));

        faixa.current = null;
        ultimoEmitido.current = valor || '';
        preencherBlocos();
        garantirParagrafos();
        renumerar();
        // O CSS e as fontes chegam depois: a medida de estouro precisa esperar.
        iframeRef.current?.contentWindow?.addEventListener('load', verificarEstouro);
        setTimeout(verificarEstouro, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valor, base, escala, desabilitado, etiqueta, cabecalho, rodape, estilos, quebra, assinaturas]);

    // Remonta quando o valor veio de fora, ou quando mudou o que só a montagem aplica.
    const montadoCom = useRef<string>('');
    useEffect(() => {
        const chave = [base, escala.toFixed(3), desabilitado, marcadores.length, cabecalho, rodape, estilos.join(','), quebra, assinaturas].join('|');
        if ((valor || '') !== ultimoEmitido.current || chave !== montadoCom.current) {
            montadoCom.current = chave;
            montar();
        }
    }, [valor, base, escala, desabilitado, marcadores.length, cabecalho, rodape, estilos, quebra, assinaturas, montar]);

    const restaurarSelecao = (): Selection | null => {
        const d = doc();
        const lista = areas();
        if (!d || lista.length === 0) return null;
        const valida = faixa.current && d.contains(faixa.current.startContainer) && areaDe(faixa.current.startContainer);
        const area = valida ? areaDe(faixa.current!.startContainer)! : lista[lista.length - 1];
        iframeRef.current?.contentWindow?.focus();
        area.focus();
        const selecao = d.getSelection();
        if (selecao) {
            selecao.removeAllRanges();
            if (valida) {
                selecao.addRange(faixa.current!);
            } else {
                const fim = d.createRange();
                fim.selectNodeContents(area);
                fim.collapse(false);
                selecao.addRange(fim);
            }
        }
        return selecao;
    };

    useImperativeHandle(ref, () => ({
        inserirMarcador: (caminho: string) => {
            if (desabilitado) return;
            const d = doc();
            const selecao = restaurarSelecao();
            if (!d || !selecao || selecao.rangeCount === 0) return;

            // Inserção direta no DOM, e não `insertHTML`: com etiqueta não editável o
            // Chrome joga o nó para fora do parágrafo quando o cursor está no fim dele.
            const faixaAtual = selecao.getRangeAt(0);
            const molde = d.createElement('template');
            molde.innerHTML = etiqueta(caminho);
            const span = molde.content.firstChild as Node;
            const depoisDaEtiqueta = d.createTextNode(ESPACO_ZERO);
            faixaAtual.deleteContents();
            faixaAtual.insertNode(depoisDaEtiqueta);
            faixaAtual.insertNode(span);
            preencherBlocos();
            garantirParagrafos();
            cursorEm(depoisDaEtiqueta, 1);
            emitir();
            verificarEstouro();
        },
        comando: (comando: string, valorComando?: string) => {
            if (desabilitado) return;
            const d = doc();
            if (!d || !restaurarSelecao()) return;
            d.execCommand(comando, false, valorComando);
            emitir();
            verificarEstouro();
        },
        quebrarPagina: () => {
            if (!desabilitado) quebrar();
        },
    }));

    return <iframe ref={iframeRef} title="Editor do contrato" className="cmf-previa-iframe"/>;
});

export default ContratoModeloEditor;
